import { useQueryClient } from "@tanstack/react-query";
import type Decimal from "decimal.js-light";
import type React from "react";
import { useCallback } from "react";

import Sidebar from "~/components/Sidebar/Sidebar";
import {
	ABSENCE_IN_TIMESPAN_BASE_QUERY_KEY,
	STAFF_MEMBERS_ABSENCES_QUERY_KEY,
} from "~/modules/absence/api/absence/absenceQueries.ts";
import type { Absence } from "~/modules/absence/api/absence/absenceTypes.ts";
import {
	STAFF_MEMBERS_VACATION_LEDGER_ENTRIES_QUERY_KEY,
	STAFF_MEMBERS_VACATION_LEDGER_REPORT_QUERY_KEY,
} from "~/modules/absence/api/vacationLedgerEntry/vacationLedgerEntryQueries.ts";
import type { WorkingSchedule } from "~/modules/absence/api/workingSchedule/workingScheduleTypes.ts";
import UpdateAbsenceForm from "~/modules/absence/components/AbsencesView/components/UpdateAbsenceForm";
import useAbsenceTypeOptions from "~/modules/absence/hooks/useAbsenceTypeOptions.tsx";

type UpdateAbsenceSidebarProps = {
	absenceData?: Absence | null;
	isOpen: boolean;
	onClose: () => void;
	remainingVacationDays: Decimal;
	staffMemberId: string,
	userCanManageLongTermAbsences: boolean;
	workingSchedules: WorkingSchedule[];
};

const UpdateAbsenceSidebar: React.FunctionComponent<UpdateAbsenceSidebarProps> = ({
	absenceData,
	isOpen,
	onClose,
	remainingVacationDays,
	staffMemberId,
	userCanManageLongTermAbsences,
	workingSchedules,
}) => {
	const queryClient = useQueryClient();
	const handleSuccess = useCallback(
		async () => {
			queryClient.removeQueries({ queryKey: [ABSENCE_IN_TIMESPAN_BASE_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_VACATION_LEDGER_ENTRIES_QUERY_KEY(staffMemberId) });
			await queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_ABSENCES_QUERY_KEY(staffMemberId) });
			await queryClient.invalidateQueries({ queryKey: STAFF_MEMBERS_VACATION_LEDGER_REPORT_QUERY_KEY(staffMemberId) });
			onClose();
		},
		[onClose, queryClient, staffMemberId],
	);

	const absenceTypeOptions = useAbsenceTypeOptions();

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={onClose}>
			{!!absenceData && <UpdateAbsenceForm
				absenceData={absenceData}
				absenceTypeSelectOptions={absenceTypeOptions}
				onSuccess={handleSuccess}
				remainingVacationDays={remainingVacationDays}
				onCancel={onClose}
				workingSchedules={workingSchedules}
				staffMemberId={staffMemberId}
				userCanManageLongTermAbsences={userCanManageLongTermAbsences}
			/>}
		</Sidebar>
	);
};

export default UpdateAbsenceSidebar;
